'use client';


import { t } from 'mobx-state-tree';
import * as THREE from 'three';

import { UndoRedoActionType } from '../../core/undoRedo/undoRedoTypes';
import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { Instance, SnapshotIn } from 'mobx-state-tree';


export const Color = t.custom<string, THREE.Color>({
  name: 'Color',
  fromSnapshot(value) {
    return new THREE.Color(value);
  },
  toSnapshot(value) {
    return '#' + value.getHexString().toUpperCase();
  },
  isTargetType(value) {
    return value instanceof THREE.Color;
  },
  getValidationMessage(value) { return 'Value must be a color string, number or THREE.Color'; }
});

/**
 * Model for color property
 * NOTE: Use ColorProperty.create() to create a color property
 */
const ColorPropertyBase = t.compose('ColorProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Color),
    value: t.optional(Color, () => new THREE.Color(1, 1, 1)),
    defaultValue: t.optional(Color, () => new THREE.Color(1, 1, 1))
  }))
  .volatile(() => ({
    /** Snapshot of value taken at the start of a drag sequence; null when not dragging. */
    preDragValue: null as THREE.Color | null,
  }))
  .views(self => ({
    get valueHex() {
      return '#' + self.value.getHexString().toUpperCase();
    },
    get hasValueChanged() {
      return !self.value.equals(self.defaultValue);
    }
  }))
  .actions(self => ({
    setValue(value: THREE.Color, fromPanel = true, initial = false, actionType: UndoRedoActionType = UndoRedoActionType.Primary) {
      if (value.equals(self.value)) return;

      if (actionType === UndoRedoActionType.Silent) {
        // Capture the pre-drag baseline on the first Silent tick of a drag sequence.
        if (self.preDragValue === null) {
          self.preDragValue = self.value.clone();
        }
        // Skip the MST write — no MobX reaction, no React re-render on every mousemove.
        self.notifyValueChange(self.path, value, { fromPanel, initial, actionType, property: self as Instance<typeof ColorProperty> });
        return;
      }

      // Primary/Secondary commit: include the pre-drag baseline so setPropertyValue
      // can record the correct undo value even though the engine already has the new value.
      const preDragValue = self.preDragValue;
      self.preDragValue = null;
      self.value = value.clone();
      self.notifyValueChange(self.path, value, { fromPanel, initial, actionType, preDragValue: preDragValue ?? undefined, property: self as Instance<typeof ColorProperty> });
    },
    setDefaultValue(value: THREE.Color) {
      self.defaultValue = value.clone();
    }
  }))
  .actions(self => ({
    resetToDefault() {
      // Clone the default value to avoid reference issues
      const defaultValue = self.defaultValue.clone();
      self.setValue(defaultValue, true, false);
    }
  }));

export const ColorProperty = t.snapshotProcessor(ColorPropertyBase, {
  preProcessor(snapshot: SnapshotIn<typeof ColorPropertyBase>) {
    return {
      ...snapshot,
      value: snapshot.value instanceof THREE.Color ? snapshot.value.clone() : new THREE.Color(1, 1, 1),
      defaultValue: snapshot.defaultValue instanceof THREE.Color ? snapshot.defaultValue.clone() : new THREE.Color(1, 1, 1)
    };
  }
});
